import { createSlice, PayloadAction, Draft } from "@reduxjs/toolkit";
import { ApiError } from "@/lib/apiClient";
import {
  BaseEntity,
  CrudState,
  CrudFilters,
  FetchOptions,
  SearchOptions,
  CreateRequest,
  BulkUpdateRequest,
  BulkDeleteRequest,
} from "@/types/crud";
import { resetStoreSpecificState } from "@/store/actions/storeActions";

export interface CreateCrudSliceConfig<
  T extends BaseEntity,
  _TFilters extends CrudFilters = CrudFilters,
> {
  name: string;
  initialState?: Partial<CrudState<T>>;
}

const createInitialState = <T extends BaseEntity>(): CrudState<T> => ({
  items: [],
  loading: false,
  error: null,
  pagination: {
    page: 0,
    total: 0,
    pageSize: 10,
    totalPages: 0,
  },
  currentItem: null,
  itemLoading: false,
  itemError: null,
  formState: {
    loading: false,
    success: false,
    error: null,
  },
  searchResults: [],
  searchLoading: false,
  searchError: null,
  searchPagination: {
    page: 0,
    total: 0,
    pageSize: 10,
    totalPages: 0,
  },
  bulkOperations: {
    loading: false,
    error: null,
    selectedIds: [],
  },
});

export const createCrudSlice = <
  T extends BaseEntity,
  TFilters extends CrudFilters = CrudFilters,
>(
  config: CreateCrudSliceConfig<T, TFilters>,
) => {
  const initialState: CrudState<T> = {
    ...createInitialState<T>(),
    ...config.initialState,
  };

  return createSlice({
    name: config.name,
    initialState,
    reducers: {
      // List operations
      fetchRequest: (
        state: Draft<CrudState<T>>,
        action: PayloadAction<FetchOptions<TFilters>>,
      ) => {
        state.loading = true;
        state.error = null;
        if (action.payload.page) {
          state.pagination.page = action.payload.page;
        }
      },
      fetchSuccess: (
        state: Draft<CrudState<T>>,
        action: PayloadAction<{
          list: T[];
          page: number;
          total: number;
          pageSize: number;
          totalPages: number;
        }>,
      ) => {
        state.items = action.payload.list as Draft<T>[];
        state.pagination.page = action.payload.page;
        state.pagination.total = action.payload.total;
        state.pagination.pageSize = action.payload.pageSize;
        state.pagination.totalPages = action.payload.totalPages;
        state.loading = false;
        state.error = null;
      },
      fetchFailure: (
        state: Draft<CrudState<T>>,
        action: PayloadAction<ApiError>,
      ) => {
        state.loading = false;
        state.error = action.payload;
      },

      // Single item operations
      fetchItemRequest: (
        state: Draft<CrudState<T>>,
        _action: PayloadAction<{ id: string }>,
      ) => {
        state.itemLoading = true;
        state.itemError = null;
      },
      fetchItemSuccess: (
        state: Draft<CrudState<T>>,
        action: PayloadAction<{ item: T }>,
      ) => {
        state.currentItem = action.payload.item as Draft<T>;
        state.itemLoading = false;
        state.itemError = null;
      },
      fetchItemFailure: (
        state: Draft<CrudState<T>>,
        action: PayloadAction<ApiError>,
      ) => {
        state.itemLoading = false;
        state.itemError = action.payload;
      },

      // Create operations
      createRequest: (
        state: Draft<CrudState<T>>,
        _action: PayloadAction<CreateRequest<T>>,
      ) => {
        state.formState.loading = true;
        state.formState.error = null;
        state.formState.success = false;
      },
      createSuccess: (
        state: Draft<CrudState<T>>,
        action: PayloadAction<{ item: T; shouldNavigate?: boolean }>,
      ) => {
        state.formState.loading = false;
        state.formState.success = true;
        state.items.unshift(action.payload.item as Draft<T>);
        state.pagination.total += 1;
        // Update current item if it's a single item view
        state.currentItem = action.payload.item as Draft<T>;
      },
      createFailure: (
        state: Draft<CrudState<T>>,
        action: PayloadAction<ApiError>,
      ) => {
        state.formState.loading = false;
        state.formState.error = action.payload;
        state.formState.success = false;
      },

      // Update operations
      updateRequest: (
        state: Draft<CrudState<T>>,
        _action: PayloadAction<{ id: string; updates: Partial<T> }>,
      ) => {
        state.formState.loading = true;
        state.formState.error = null;
        state.formState.success = false;
      },
      updateSuccess: (
        state: Draft<CrudState<T>>,
        action: PayloadAction<{ item: T; shouldNavigate?: boolean }>,
      ) => {
        state.formState.loading = false;
        state.formState.success = true;
        const index = state.items.findIndex(
          (item) => item.id === action.payload.item.id,
        );
        if (index !== -1) {
          state.items[index] = action.payload.item as Draft<T>;
        }
        if (state.currentItem?.id === action.payload.item.id) {
          state.currentItem = action.payload.item as Draft<T>;
        }
      },
      updateFailure: (
        state: Draft<CrudState<T>>,
        action: PayloadAction<ApiError>,
      ) => {
        state.formState.loading = false;
        state.formState.error = action.payload;
        state.formState.success = false;
      },

      // Delete operations
      deleteRequest: (
        state: Draft<CrudState<T>>,
        _action: PayloadAction<{ id: string }>,
      ) => {
        state.formState.loading = true;
        state.formState.error = null;
      },
      deleteSuccess: (
        state: Draft<CrudState<T>>,
        action: PayloadAction<{ id: string }>,
      ) => {
        state.formState.loading = false;
        state.formState.success = true;
        state.items = state.items.filter(
          (item) => item.id !== action.payload.id,
        ) as Draft<T>[];
        state.pagination.total = Math.max(0, state.pagination.total - 1);
        if (state.currentItem?.id === action.payload.id) {
          state.currentItem = null;
        }
      },
      deleteFailure: (
        state: Draft<CrudState<T>>,
        action: PayloadAction<ApiError>,
      ) => {
        state.formState.loading = false;
        state.formState.error = action.payload;
      },

      // Search operations
      searchRequest: (
        state: Draft<CrudState<T>>,
        _action: PayloadAction<SearchOptions<TFilters>>,
      ) => {
        state.searchLoading = true;
        state.searchError = null;
      },
      searchSuccess: (
        state: Draft<CrudState<T>>,
        action: PayloadAction<{
          list: T[];
          page: number;
          total: number;
          pageSize: number;
          totalPages: number;
        }>,
      ) => {
        state.searchResults = action.payload.list as Draft<T>[];
        state.searchPagination.page = action.payload.page;
        state.searchPagination.total = action.payload.total;
        state.searchPagination.pageSize = action.payload.pageSize;
        state.searchPagination.totalPages = action.payload.totalPages;
        state.searchLoading = false;
      },
      searchFailure: (
        state: Draft<CrudState<T>>,
        action: PayloadAction<ApiError>,
      ) => {
        state.searchError = action.payload;
        state.searchLoading = false;
      },

      // Bulk operations
      bulkUpdateRequest: (
        state: Draft<CrudState<T>>,
        _action: PayloadAction<BulkUpdateRequest>,
      ) => {
        state.bulkOperations.loading = true;
        state.bulkOperations.error = null;
      },
      bulkUpdateSuccess: (
        state: Draft<CrudState<T>>,
        action: PayloadAction<{ updatedItems: T[] }>,
      ) => {
        state.bulkOperations.loading = false;
        action.payload.updatedItems.forEach((updatedItem) => {
          const index = state.items.findIndex(
            (item) => item.id === updatedItem.id,
          );
          if (index !== -1) {
            state.items[index] = updatedItem as Draft<T>;
          }
        });
        state.bulkOperations.selectedIds = [];
      },
      bulkUpdateFailure: (
        state: Draft<CrudState<T>>,
        action: PayloadAction<ApiError>,
      ) => {
        state.bulkOperations.loading = false;
        state.bulkOperations.error = action.payload;
      },

      bulkDeleteRequest: (
        state: Draft<CrudState<T>>,
        _action: PayloadAction<BulkDeleteRequest>,
      ) => {
        state.bulkOperations.loading = true;
        state.bulkOperations.error = null;
      },
      bulkDeleteSuccess: (
        state: Draft<CrudState<T>>,
        action: PayloadAction<{ deletedIds: string[] }>,
      ) => {
        state.bulkOperations.loading = false;
        state.items = state.items.filter(
          (item) => !action.payload.deletedIds.includes(item.id!),
        ) as Draft<T>[];
        state.pagination.total = Math.max(
          0,
          state.pagination.total - action.payload.deletedIds.length,
        );
        state.bulkOperations.selectedIds = [];
      },
      bulkDeleteFailure: (
        state: Draft<CrudState<T>>,
        action: PayloadAction<ApiError>,
      ) => {
        state.bulkOperations.loading = false;
        state.bulkOperations.error = action.payload;
      },

      // Utility operations
      clearCurrentItem: (state: Draft<CrudState<T>>) => {
        state.currentItem = null;
        state.itemError = null;
      },
      clearSearchResults: (state: Draft<CrudState<T>>) => {
        state.searchResults = [];
        state.searchError = null;
      },
      resetFormState: (state: Draft<CrudState<T>>) => {
        state.formState.loading = false;
        state.formState.error = null;
        state.formState.success = false;
      },
      setSelectedIds: (
        state: Draft<CrudState<T>>,
        action: PayloadAction<string[]>,
      ) => {
        state.bulkOperations.selectedIds = action.payload;
      },
    },
    extraReducers: (builder) => {
      builder.addCase(resetStoreSpecificState, () => {
        console.log(`[createCrudSlice:${name}] 🔄 Received resetStoreSpecificState, resetting to initialState`);
        return initialState;
      });
    },
  });
};

export type CrudSliceReturn<
  T extends BaseEntity,
  TFilters extends CrudFilters = CrudFilters,
> = ReturnType<typeof createCrudSlice<T, TFilters>>;
